<template>
  <div>
    <DxPopup
      :visible="visible"
      @hiding="onHiding"
      @shown="popupShown"
      :width="400"
      :height="215"
      :show-title="true"
      :title="popupTitle"
      :drag-enabled="false"
      :close-on-outside-click="false"
      :show-close-button="true"
      :wrapperAttr="{ class: 'css-v2 gc-popup gc-popup-save-layout' }"
    >
      <div class="css-v2 gc-popup-form" ref="mainDiv">
        <keep-alive>
          <gc-form
            :key="formKey"
            :form-options="{
              gcLabelLocation: 'left',
            }"
            ref="gc-header-form"
            :form-items="getHeaderFormItems"
            :is-normal="true"
            :data-model.sync="model"
            :form-mode="'create'"
            :from-from="'gc-popup-save-layout'"
            :form-height="130"
            :formContentReady="formContentReady"
            :valueChangeCallBack="valueChangeCallBack"
            :checkValid="isValid"
            :validationGroup="'saveLayoutGroup'"
          />
        </keep-alive>
      </div>
    </DxPopup>
  </div>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { mapGetters } from "vuex";
import { DataGridState } from "../../_models/data-grid-state";
import { secureStorage } from "../../_services/storage";

export default {
  name: "SaveGridLayoutPopup",
  components: {
    DxPopup,
  },
  props: {
    visible: {
      type: Boolean,
      default: false, // Default to false to ensure the popup is hidden when not explicitly shown
    },
    persistenceKey: {
      type: String,
      required: true, // Ensure that persistenceKey is always provided
    },
    showLoading: {
      type: Function,
      default: () => {}, // Default to an empty function to avoid errors if not provided
    },
    originalColumns: {
      type: Array,
      default: () => [], // Default to an empty array to avoid errors if not provided
      required: true, // Ensure that originalColumns is always provided
    },
  },
  data() {
    return {
      popupTitle: "Save New View",
      formKey: new Date().getTime(), // To reset the form
      model: {
        viewName: "",
        visibility: null,
      },
      isValid: false,
    };
  },
  methods: {
    onHiding() {
      this.model.viewName = "";
      this.model.visibility = null;
      this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
      this.$emit("update:visible", false);
    },
    popupShown() {
      this.$nextTick(() => {
        this.$store.dispatch("hotKey/setHotKeyPopupMode", true);

        if (!this.isAdminUser) {
          this.model.visibility = "Me"; // Default to "Only Me" for non-admin users
        }
      });
    },
    formContentReady(e) {
      setTimeout(() => {
        this.$nextTick(() => {
          const formInstance = e.component;
          const viewNameEditor = formInstance.getEditor("viewName");
          if (viewNameEditor) {
            viewNameEditor.focus();
          }
        });
      }, 300);
    },

    onSaveButtonClick() {
      this.isValid = true;
    },

    async valueChangeCallBack(isValid, brokenRules) {
      this.isValid = false;

      var validations = "";

      if (!isValid) {
        brokenRules.forEach((element) => {
          validations += `<div style="width: 400px;">${element.message}</div>`;
        });

        try {
          await this.$alert(validations, "Validation Error", {
            width: 400,
          });
        } catch (error) {
          // User might have pressed ESC, but we still need to reset
        }

        this.$nextTick(() => {
          this.$refs["mainDiv"].focus();
        });
      } else {
        this.showLoading(true);
        try {
          const response = await DataGridState.saveStateAndFilterToDatabase(
            this.persistenceKey,
            this.model.viewName,
            this.model.visibility,
            "New",
            this.originalColumns,
          );

          if (response) {
            this.$notify("Grid layout saved successfully!", "success");
            this.$nextTick(() => {
              this.$emit("update:visible", false);
            });
          }
        } catch (error) {
          this.showLoading(false);
          this.$store.dispatch("ui/setServerError", {
            error: error,
            title: "Failed",
          });
        } finally {
          this.showLoading(false);
        }
      }
    },
  },
  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),
    getHeaderFormItems() {
      return [
        {
          dataField: "viewName",
          label: {
            text: "View Name",
          },
          editorOptions: {
            placeholder: "Enter View Name",
            maxLength: 200,
            tabIndex: 0,
          },
          required: true,
        },
        {
          dataField: "visibility",
          editorType: "dxSelectBox",
          label: {
            text: "Visibility",
          },
          editorOptions: {
            items: [
              { id: "Me", text: "Only Me" },
              {
                id: "Everyone",
                text: "For Everyone",
                disabled: !this.isAdminUser,
              }, // Disable "For Everyone" option for non-admin users
            ],
            displayExpr: "text",
            valueExpr: "id",
            placeholder: "Select Visibility",
            searchEnabled: true,
            readOnly: !this.isAdminUser, // Make the select box read-only for non-admin users
            tabIndex: this.isAdminUser ? 1 : -1, // Remove from tab order for non-admin users
          },
          required: true,
        },
        {
          itemType: "group",
          caption: "",
          colCount: 2,
          cssClass: "gc-save-cancel-button-group",
          items: [
            {
              itemType: "button",
              horizontalAlignment: "center",
              cssClass: "gc-cancel-button",
              buttonOptions: {
                text: "Cancel",
                type: "normal",
                width: 70,
                onClick: () => {
                  this.$emit("update:visible", false);
                },
              },
            },
            {
              itemType: "button",
              horizontalAlignment: "center",
              cssClass: "gc-save-button",
              buttonOptions: {
                text: "Save",
                type: "success",
                width: 70,
                useSubmitBehavior: false,
                onClick: () => {
                  this.onSaveButtonClick();
                },
              },
            },
          ],
        },
      ];
    },
    isAdminUser() {
      return secureStorage.get("isAdmin");
    },
  },

  watch: {
    getHotKeySave: {
      handler(val) {
        if (val) {
          this.$store.dispatch("hotKey/setHotKeySave", false);
          this.$nextTick(() => {
            // Trigger validation which will call valueChangeCallBack
            this.isValid = true;
          });
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.gc-popup-save-layout {
  .dx-layout-manager.dx-widget {
    padding-right: unset !important;
  }

  .gc-save-cancel-button-group {
    display: flex;
    justify-content: flex-end;

    .dx-field-item-content.dx-field-item-content-location-right {
      padding-top: 10px !important;
    }

    .dx-button-content {
      padding: 5px 5px !important;
    }

    .dx-button-text {
      font-size: 12px !important;
      padding: unset !important;
    }

    .gc-cancel-button {
      text-align: end;
      padding-right: 5px !important;
    }

    .gc-save-button {
      text-align: end !important;
      padding-left: 5px !important;
      .dx-button-submit-input {
        padding: unset !important;
      }

      .dx-button {
        background-color: #03a9f4;
        color: #fff !important;
        width: 70px !important;
      }
    }
  }
}
</style>
